// Clock-in Status Integration Service
// This service handles fetching and updating clock-in status from an internal website
import { CLOCK_IN_CONFIG, getApiUrl, getHeaders, mapStatus, log } from '../config/clockInConfig';

class ClockInService {
  constructor() {
    this.baseUrl = CLOCK_IN_CONFIG.API_BASE_URL;
    this.pollingInterval = null;
    this.isPolling = false;
    this.consecutiveFailures = 0;
  }

  /**
   * Fetch clock-in status for all staff members
   * @returns {Promise<Object>} Object with staff login as key and status as value
   */
  async fetchClockInStatus() {
    try {
      const response = await fetch(getApiUrl(CLOCK_IN_CONFIG.ENDPOINTS.GET_ALL_STATUS), {
        method: 'GET',
        headers: getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.consecutiveFailures = 0; // Reset failure count on success
      log('info', 'Successfully fetched clock-in status');
      return this.parseClockInData(data);
    } catch (error) {
      this.consecutiveFailures++;
      log('error', 'Error fetching clock-in status:', error);
      
      if (this.consecutiveFailures >= CLOCK_IN_CONFIG.ERROR_HANDLING.MAX_CONSECUTIVE_FAILURES) {
        log('warn', `Stopping polling after ${this.consecutiveFailures} consecutive failures`);
        this.stopPolling();
      }
      
      throw error;
    }
  }

  /**
   * Fetch clock-in status for specific staff members
   * @param {Array<string>} staffLogins - Array of staff login IDs
   * @returns {Promise<Object>} Object with staff login as key and status as value
   */
  async fetchClockInStatusForStaff(staffLogins) {
    try {
      const response = await fetch(`${this.baseUrl}/clock-in-status/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_CLOCK_IN_API_KEY || ''}`,
        },
        credentials: 'include',
        body: JSON.stringify({ staffLogins }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return this.parseClockInData(data);
    } catch (error) {
      console.error('Error fetching clock-in status for specific staff:', error);
      throw error;
    }
  }

  /**
   * Parse the clock-in data from the API response
   * @param {Object} data - Raw API response data
   * @returns {Object} Parsed status data
   */
  parseClockInData(data) {
    const statusMap = {};
    
    // Adjust this parsing logic based on your API response structure
    if (data.staff && Array.isArray(data.staff)) {
      data.staff.forEach(staff => {
        statusMap[staff.login] = this.mapStatusToAppStatus(staff.status);
      });
    } else if (typeof data === 'object') {
      // If data is already in the format { login: status }
      Object.keys(data).forEach(login => {
        statusMap[login] = this.mapStatusToAppStatus(data[login]);
      });
    }

    return statusMap;
  }

  /**
   * Map external status values to app status values
   * @param {string} externalStatus - Status from external API
   * @returns {string} App status value
   */
  mapStatusToAppStatus(externalStatus) {
    return mapStatus(externalStatus);
  }

  /**
   * Start polling for clock-in status updates
   * @param {Function} onStatusUpdate - Callback function when status updates
   * @param {number} intervalMs - Polling interval in milliseconds (default: 30 seconds)
   */
  startPolling(onStatusUpdate, intervalMs = 30000) {
    if (this.isPolling) {
      this.stopPolling();
    }

    this.isPolling = true;
    
    // Initial fetch
    this.fetchClockInStatus()
      .then(statusMap => onStatusUpdate(statusMap))
      .catch(error => console.error('Initial clock-in status fetch failed:', error));

    // Set up polling interval
    this.pollingInterval = setInterval(async () => {
      try {
        const statusMap = await this.fetchClockInStatus();
        onStatusUpdate(statusMap);
      } catch (error) {
        console.error('Clock-in status polling failed:', error);
      }
    }, intervalMs);
  }

  /**
   * Stop polling for clock-in status updates
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
  }

  /**
   * Update clock-in status for a specific staff member
   * @param {string} staffLogin - Staff login ID
   * @param {string} status - New status
   * @returns {Promise<boolean>} Success status
   */
  async updateClockInStatus(staffLogin, status) {
    try {
      const response = await fetch(`${this.baseUrl}/clock-in-status/${staffLogin}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_CLOCK_IN_API_KEY || ''}`,
        },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error updating clock-in status:', error);
      throw error;
    }
  }

  /**
   * Get the current polling status
   * @returns {boolean} Whether polling is active
   */
  isPollingActive() {
    return this.isPolling;
  }
}

// Create and export a singleton instance
const clockInService = new ClockInService();
export default clockInService; 